import React from "react";
import { RefreshCw, ShieldCheck, GraduationCap, Headphones, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";

const benefits = [
  {
    icon: RefreshCw,
    title: "Website Updates",
    description:
      "Need help making an update on your website? Let us know. Many of the quick updates you may need are included in your hosting package!",
  },
  {
    icon: ShieldCheck,
    title: "Security & Confidence",
    description:
      "We update your website WordPress Core, Themes, & Plugins regularly to keep your website safe. Nightly Backups with 30 days of retained backups to make disaster recovery easy.",
  },
  {
    icon: GraduationCap,
    title: "Training",
    description:
      "If you want to learn how to update your own website, we can point you in the right direction and get you started.",
  },
  {
    icon: Headphones,
    title: "Support",
    description:
      "During business hours – we can usually reply to your email support within the hour. Whatever comes up we want to help you get back to focusing on your business!",
  },
];

export default function HostingDetails() {
  const handleScroll = (href) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="hosting" className="py-20 sm:py-28">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto text-center mb-14 sm:mb-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <p className="text-sm font-semibold uppercase tracking-widest text-primary mb-3">
              Managed Hosting
            </p>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight mb-6">
              We take care of everything for you
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed">
              Hosting your website doesn't need to be a huge hassle.{" "}
              <span className="font-semibold text-foreground">
                You want to focus on your business.
              </span>{" "}
              So don't waste your time worrying about your website.
            </p>
            <p className="mt-4 text-muted-foreground leading-relaxed">
              We specialize in custom & managed hosting solutions for WordPress
              and PHP/mySQL-based websites. The key is "managed" which means{" "}
              <span className="font-semibold text-foreground">
                we take care of everything for you
              </span>{" "}
              including domain name configuration. Your site is secure &
              protected with regular backups.
            </p>
          </motion.div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-8 max-w-4xl mx-auto">
          {benefits.map((benefit, i) => (
            <motion.div
              key={benefit.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              className="flex gap-4 p-6 rounded-2xl bg-muted/40 border border-border/30 hover:border-primary/15 transition-all"
            >
              <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-primary/[0.08] flex items-center justify-center">
                <benefit.icon className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h3 className="font-bold mb-1.5">{benefit.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {benefit.description}
                </p>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="mt-12 sm:mt-16 text-center"
        >
          <div className="block">
            <Button
              onClick={() => handleScroll("#contact")}
              size="lg"
              className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-full px-8 py-6 text-base font-semibold shadow-lg shadow-primary/20"
            >
              Have some questions? Contact us to start the conversation!
            </Button>
          </div>
        </motion.div>
      </div>
    </section>
  );
}